import type { SupabaseClient } from "@supabase/supabase-js";

// Cache module (mémoire process, pas de persistance) : évite de re-questionner
// la base à chaque montage de ProtectedRoute pendant la session. On ne
// mémorise QUE les acceptations CONFIRMÉES — jamais une absence/erreur, qui
// pourrait devenir stale (acceptation faite entre-temps depuis un autre onglet).
const acceptedCache = new Set<string>();

function cacheKey(userId: string, version: string): string {
  return `${userId}:${version}`;
}

export function resetTermsCacheForTests(): void {
  acceptedCache.clear();
}

export type FetchTermsAcceptedResult =
  | { ok: true; accepted: boolean }
  | { ok: false; error: string };

export async function fetchTermsAccepted(
  client: SupabaseClient,
  userId: string,
  version: string,
): Promise<FetchTermsAcceptedResult> {
  const key = cacheKey(userId, version);
  if (acceptedCache.has(key)) return { ok: true, accepted: true };

  const { data, error } = await client
    .from("terms_acceptances")
    .select("id")
    .eq("user_id", userId)
    .eq("version", version)
    .maybeSingle();

  if (error) return { ok: false, error: error.message };
  if (data) {
    acceptedCache.add(key);
    return { ok: true, accepted: true };
  }
  return { ok: true, accepted: false };
}

export type RecordTermsAcceptanceResult = { ok: true } | { ok: false; error: string };

export async function recordTermsAcceptance(
  client: SupabaseClient,
  userId: string,
  version: string,
): Promise<RecordTermsAcceptanceResult> {
  // ignoreDuplicates : idempotent si l'usager accepte depuis deux onglets — la
  // contrainte unique (user_id, version) protège déjà, l'upsert évite juste
  // l'erreur de conflit côté client.
  const { error } = await client
    .from("terms_acceptances")
    .upsert(
      { user_id: userId, version },
      { onConflict: "user_id,version", ignoreDuplicates: true },
    );

  if (error) return { ok: false, error: error.message };
  acceptedCache.add(cacheKey(userId, version));
  return { ok: true };
}
